<template>
  <div
    class="relative flex h-[50px] shrink-0 select-none items-center rounded-8 bg-bg-base-bg4"
    :class="isImage
      ? 'w-48 overflow-visible p-0 phone:size-[50px] phone:w-[50px]'
      : 'w-48 overflow-visible p-2'"
  >
    <div
      class="relative shrink-0 overflow-hidden"
      :class="isImage ? 'size-[50px] rounded-8' : 'size-[34px] rounded-8'"
    >
      <file-icon
        class="h-full w-full"
        :mime="file.file.type"
        :cookie="file.name"
        :name="file.file.name"
        :is-loaded="unref(file.isLoaded)"
        :open-preview-on-click="false"
        :object-fit="isImage ? 'cover' : 'contain'"
        :bordered="false"
      />
    </div>

    <div
      class="relative min-w-0 flex-1 truncate"
      :class="isImage ? 'px-2 phone:hidden' : 'pl-2'"
    >
      <p class="truncate text-body-m-14-20 text-fg-base-fg3">
        {{ fileNameWithoutExtension }}
      </p>
      <div
        :class="{
          'text-warning': unref(file.isError),
          'error-message': unref(file.isError),
        }"
        class="truncate text-label-r-13-18 text-fg-base-fg17"
        :data-message="errorText"
      >
        {{ fileMeta }}
        <span
          v-if="unref(file.isError)"
          data-test="uploading-file-item-message"
        >{{ errorText }}</span>
      </div>
    </div>

    <button
      type="button"
      data-test="uploading-file-item-delete-icon"
      class="
        absolute -right-1.5 -top-1.5 z-10 flex size-[22px] items-center justify-center
        rounded-[22px] bg-surf-neut-base-def text-white
      "
      @click.stop="emit('delete')"
    >
      <wui-icon
        name="closeRegularSmM"
        class="size-3"
      />
      <span class="sr-only">{{ $t('common.removeFile') }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { computed, unref, Ref } from 'vue'

import { FileIcon } from '@/entities/chat-event-file'

import { useLocalization } from '@/shared/composables/localization'
import { usePromptServer } from '@/shared/composables/web-dav'
import { ChatFile } from '@/shared/types/files'
import { bytesConverter } from '@/shared/utils/bytes-converter'
import { getFileTypeByName, IMAGE_TYPE } from '@/shared/utils/files'

const props = defineProps<{
  file: ChatFile,
  index: number,
}>()

const emit = defineEmits<{
  (event: 'delete'): void;
}>()

const { t } = useLocalization()

const isImage = computed(() => getFileTypeByName(props.file.file.name) === IMAGE_TYPE)

const formatProgressSize = (progress: Ref<number>, size: number) => {
  const value = unref(progress)
  if (size < 1024) {
    return `${Math.round(size / 100 * value)}/${size} ${t('common.bytes')}`
  }
  if (size < 1_000_000) {
    return `${Math.round(size / 1024 / 100 * value)}/${Math.round(size / 1024)} ${t('common.kb')}`
  }
  if (size < 1_000_000_000) {
    return `${Math.round(size / 1024 / 1024 / 100 * value)}/${Math.round(size / 1024 / 1024)} ${t('common.mb')}`
  }
  if (size < 1_000_000_000_000) {
    return `${Math.round(size / 1024 / 1024 / 1024 / 100 * value)}/${Math.round(size / 1024 / 1024 / 1024)} ${t('common.gb')}`
  }
  return `${Math.round(size / 1024 / 100 * value)}/${Math.round(size / 1024)} ${t('common.kb')}`
}

const { maxUploadFileSize } = usePromptServer()
const errorText = computed(() => {
  if (props.file.file.size > maxUploadFileSize()) {
    return `${t('common.maxTypeFileSize')} - ${maxUploadFileSize() / 1024 / 1024} ${t('common.mb')}`
  }

  return t('chat.fileUploadError')
})

const fileNameWithoutExtension = computed(() => {
  const fileNameAndExtension = props.file.file.name.split('.')
  fileNameAndExtension.pop()
  return fileNameAndExtension.join('.')
})

const fileExtension = computed(() => {
  return props.file.file.name.split('.').pop()?.toUpperCase() ?? ''
})

const fileMeta = computed(() => {
  const extension = fileExtension.value
  if (unref(props.file.isLoaded)) {
    return `${bytesConverter(props.file.file.size)}, ${extension}`
  }
  return `${formatProgressSize(props.file.progress, props.file.file.size)}, ${extension}`
})
</script>

<style scoped>
.error-message:hover:before {
  content: attr(data-message);
  position: absolute;
  top: -4rem;
  left: -3.1rem;
  background-color: var(--color-white);
  border-radius: 5px;
  padding: 1rem;
}
</style>
